/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, Text } from '@components/atoms'

import { useAppSelector, useAppDispatch, logDashboardActions } from '@redux/index'
import type { LogSummaryType } from '@redux/types'

import { blue, red, green, grey } from '@mui/material/colors'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'

const LogDashboardPage = () => {
	const dispatch = useAppDispatch()

	const summaryDate = useAppSelector(state => state.logDashboard.summaryDate)
	const summary: LogSummaryType | null = useAppSelector(
		state => state.logDashboard.summary,
	)
	const summaryStatus = useAppSelector(state => state.logDashboard.summaryStatus)

	React.useEffect(() => {
		dispatch(logDashboardActions.TRIGGER_GET_SUMMARY(true))
	}, [summaryDate])

	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(logDashboardActions.SUMMARY_DATE(e.target.value))
	}

	const isLoading = summaryStatus === 'loading'

	return (
		<View css={style}>
			<BoxView title="API 로그 대시보드">
				<View className="dashboardContainer">
					{/* Date Picker */}
					<View className="datePickerRow">
						<TextField
							type="date"
							label="날짜"
							value={summaryDate}
							onChange={handleDateChange}
							size="small"
							InputLabelProps={{ shrink: true }}
						/>
					</View>

					{isLoading ? (
						<View className="loadingView">
							<CircularProgress />
						</View>
					) : (
						<>
							{/* Summary Cards */}
							<View className="cardsRow">
								<View className="card" style={{ borderTop: `4px solid ${blue['A400']}` }}>
									<Text category="p2" style={{ color: grey[600] }}>
										총 요청수
									</Text>
									<Text category="h3" style={{ color: blue['A400'], fontWeight: 700 }}>
										{summary?.totalRequests?.toLocaleString() ?? 0}
									</Text>
								</View>
								<View className="card" style={{ borderTop: `4px solid ${red['A400']}` }}>
									<Text category="p2" style={{ color: grey[600] }}>
										에러수
									</Text>
									<Text category="h3" style={{ color: red['A400'], fontWeight: 700 }}>
										{summary?.totalErrors?.toLocaleString() ?? 0}
									</Text>
								</View>
								<View
									className="card"
									style={{ borderTop: `4px solid ${green['A400']}` }}
								>
									<Text category="p2" style={{ color: grey[600] }}>
										평균 응답시간
									</Text>
									<Text category="h3" style={{ color: green[700], fontWeight: 700 }}>
										{`${summary?.avgResponseTime ?? 0}ms`}
									</Text>
								</View>
							</View>

							{/* Charts and Tables */}
							<View className="contentRow">
								{/* Country Top 10 */}
								<View className="halfSection">
									<Text category="s1" style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
										국가별 요청 TOP 10
									</Text>
									<TableContainer component={Paper} variant="outlined">
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>#</TableCell>
													<TableCell>국가</TableCell>
													<TableCell align="right">요청수</TableCell>
													<TableCell>비율</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{(summary?.topCountries ?? []).map((item, idx) => {
													const pct = summary?.totalRequests
														? ((item.count / summary.totalRequests) * 100).toFixed(1)
														: '0'
													return (
														<TableRow key={item.country}>
															<TableCell>{idx + 1}</TableCell>
															<TableCell>{item.country || 'Unknown'}</TableCell>
															<TableCell align="right">
																{item.count.toLocaleString()}
															</TableCell>
															<TableCell>
																<View className="barWrapper">
																	<View
																		className="bar"
																		style={{
																			width: `${pct}%`,
																			backgroundColor: blue['A400'],
																		}}
																	/>
																	<Text category="c2" style={{ marginLeft: '0.25rem' }}>
																		{`${pct}%`}
																	</Text>
																</View>
															</TableCell>
														</TableRow>
													)
												})}
												{(summary?.topCountries ?? []).length === 0 && (
													<TableRow>
														<TableCell colSpan={4} align="center">
															데이터 없음
														</TableCell>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</View>

								{/* API Top 10 */}
								<View className="halfSection">
									<Text category="s1" style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
										API 요청 TOP 10
									</Text>
									<TableContainer component={Paper} variant="outlined">
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>#</TableCell>
													<TableCell>API</TableCell>
													<TableCell align="right">요청수</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{(summary?.topApis ?? []).map((item, idx) => (
													<TableRow key={item.api}>
														<TableCell>{idx + 1}</TableCell>
														<TableCell
															style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
														>
															{item.api}
														</TableCell>
														<TableCell align="right">
															{item.count.toLocaleString()}
														</TableCell>
													</TableRow>
												))}
												{(summary?.topApis ?? []).length === 0 && (
													<TableRow>
														<TableCell colSpan={3} align="center">
															데이터 없음
														</TableCell>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</View>
							</View>

							{/* Error List */}
							<View className="errorSection">
								<Text category="s1" style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
									에러 목록
								</Text>
								<TableContainer component={Paper} variant="outlined">
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>시간</TableCell>
												<TableCell>API</TableCell>
												<TableCell>국가</TableCell>
												<TableCell>상태코드</TableCell>
												<TableCell>에러메시지</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{(summary?.errors ?? []).map((err, idx) => (
												<TableRow key={`${err.requestId}-${idx}`}>
													<TableCell style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
														{err.timestamp}
													</TableCell>
													<TableCell
														style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
													>
														{err.method} {err.url}
													</TableCell>
													<TableCell>{err.country || '-'}</TableCell>
													<TableCell>
														<Chip
															label={err.statusCode}
															size="small"
															color="error"
															variant="outlined"
														/>
													</TableCell>
													<TableCell
														style={{
															fontSize: '0.8rem',
															maxWidth: 300,
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
														}}
													>
														{err.errorMessage}
													</TableCell>
												</TableRow>
											))}
											{(summary?.errors ?? []).length === 0 && (
												<TableRow>
													<TableCell colSpan={5} align="center">
														에러 없음
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</View>
						</>
					)}
				</View>
			</BoxView>
		</View>
	)
}

export default LogDashboardPage

const style = css({
	'.dashboardContainer': {
		width: '100%',
		padding: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1.5rem',
	},

	'.datePickerRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '1rem',
	},

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '20rem',
	},

	'.cardsRow': {
		display: 'flex',
		gap: '1rem',
	},

	'.card': {
		flex: 1,
		padding: '1.5rem',
		backgroundColor: '#fff',
		borderRadius: '8px',
		boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
	},

	'.contentRow': {
		display: 'flex',
		gap: '1rem',
	},

	'.halfSection': {
		flex: 1,
	},

	'.errorSection': {
		width: '100%',
	},

	'.barWrapper': {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
	},

	'.bar': {
		height: '8px',
		borderRadius: '4px',
		minWidth: '2px',
	},
})
